<template>
  <div class="run-list">
    <BasePreloader v-if="loading && !runs.length" color="purple" size="l"/>
    <p v-else-if="!runs.length" class="run-list__empty">
      {{ emptyText || (showWho ? $t('carriers.import.history.EmptyAll') : $t('carriers.import.history.Empty')) }}
    </p>

    <div v-else>
      <!-- One row per read: one job, or the two a "Read both" started together (groupReads).
           The row is judged by its running half while one runs, by its policies half once
           both are done; each half keeps its own counts line, details and buttons. -->
      <div
        v-for="read in reads"
        :key="read.key"
        class="run-list__run"
        :class="{ 'run-list__run--current': read.runs.some(isCurrent) }"
      >
        <div class="run-list__head" @click="toggle(read)">
          <span class="run-list__badge" :class="`run-list__badge--${tone(read.lead)}`">{{ badge(read.lead) }}</span>

          <div class="run-list__main">
            <p class="run-list__when">
              {{ formatWhen(read.lead.finishedAt || read.lead.updatedAt, $i18n.locale) }}
              <span class="run-list__kind">· {{ kindLabel(read) }}</span>
              <span v-if="isDailyRun(read.lead)" class="run-list__daily">{{ $t('carriers.import.daily.Badge') }}</span>
              <template v-if="read.runs.length === 1">
                <span v-if="isStopping(read.lead)" class="run-list__live">
                  · {{ $t('carriers.import.state.stopping') }}
                </span>
                <span v-else-if="stoppedEarly(read.lead)" class="run-list__partial">
                  · {{ $t('carriers.import.history.StoppedEarly') }}
                </span>
              </template>
              <span v-if="expandable(read)" class="run-list__chevron">
                {{ expanded === read.key ? '▴' : '▾' }}
              </span>
            </p>

            <p v-if="showWho" class="run-list__who">
              <CarrierLogo :src="logoFor ? logoFor(read.lead) : null" :size="20"/>
              <span class="run-list__carrier">{{ read.lead.carrierName || $t('carriers.import.TheCarrier') }}</span>
              <span v-if="read.lead.scrapeAccountLabel" class="run-list__login">· {{ read.lead.scrapeAccountLabel }}</span>
            </p>

            <div
              v-for="run in read.runs"
              :key="run.scrapeJobId"
              class="run-list__half"
              :class="{ 'run-list__half--paired': read.runs.length > 1 }"
            >
              <!-- Which half this is, and how it ended, when the read has two. -->
              <p v-if="read.runs.length > 1" class="run-list__half-kicker">
                {{ $t(`carriers.import.kind.${kindOf(run)}`) }}
                <span v-if="isStopping(run)" class="run-list__live">· {{ $t('carriers.import.state.stopping') }}</span>
                <span v-else-if="stoppedEarly(run)" class="run-list__partial">· {{ $t('carriers.import.history.StoppedEarly') }}</span>
              </p>

              <!-- A live run counts profiles, not policies: the counts below stay at
                   zero until the import starts writing, which is most of a run. -->
              <p v-if="isJobActive(run)" class="run-list__sum">
                <template v-if="progressOf(run)">
                  <b>{{ progressFigure(run) }}</b>
                  {{ $t(progressTotalKey(run), { total: formatCount(progressOf(run).total) }) }}
                  <span v-if="etaOf(run)" class="run-list__muted">· {{ etaOf(run) }}</span>
                </template>
                <template v-else>{{ unknownProgressText(run) }}</template>
              </p>

              <!-- A payments read counts what it did to schedules, not policies
                   created and updated: its own line. A run from before those
                   counts were kept says only that it read, never "nothing
                   changed" -- that was not known. -->
              <p v-else-if="kindOf(run) === 'PAYMENTS' && finished(run)" class="run-list__sum">
                <template v-if="paymentsLineOf(run)">
                  <template v-for="(piece, i) in paymentsLineOf(run)">
                    <span :key="piece.key"><b>{{ formatCount(piece.count) }}</b> {{ $t(piece.label) }}</span
                    ><span v-if="i < paymentsLineOf(run).length - 1" :key="`${piece.key}-sep`"> · </span>
                  </template>
                </template>
                <template v-else>
                  {{ $t('carriers.import.summary.PaymentsRead', { carrier: run.carrierName || $t('carriers.import.TheCarrier') }) }}
                </template>
              </p>

              <!-- One sentence with the numbers slotted in, not four fragments
                   concatenated: a translator has to be able to reorder and inflect
                   the words around each count. -->
              <i18n
                v-else-if="hasFullCounts(run)"
                path="carriers.import.history.CountsFull"
                tag="p"
                class="run-list__sum"
              >
                <template #read><b>{{ formatCount(run.totalReadCount) }}</b></template>
                <template #imported><b>{{ formatCount(run.policiesCreatedCount) }}</b></template>
                <template #updated><b>{{ formatCount(run.policiesUpdatedCount) }}</b></template>
                <template #skipped><b>{{ formatCount(run.skippedCount) }}</b></template>
              </i18n>
              <!-- Runs from before the counts were split apart kept only the
                   created+updated total, so they can honestly say no more. -->
              <i18n
                v-else-if="hasCounts(run)"
                path="carriers.import.history.Counts"
                tag="p"
                class="run-list__sum"
              >
                <template #imported><b>{{ formatCount(run.importedCount) }}</b></template>
                <template #skipped><b>{{ formatCount(run.skippedCount) }}</b></template>
              </i18n>

              <p v-else class="run-list__sum">{{ line(run) }}</p>

              <!-- A group's members are policies the run wrote too: counted above, and named here
                   so "41 imported" from one colectivo is not a mystery. After the chain above, not
                   inside it: a v-if between two v-else-ifs splits them in two, and a run then
                   showed its counts line and the legacy one both. -->
              <p v-if="!isJobActive(run) && hasFullCounts(run) && memberPoliciesLine(run)" class="run-list__ml-sub">
                {{ memberPoliciesLine(run) }}
              </p>

              <!-- The policies behind the counts, on the card itself rather than behind the chevron:
                   a line of numbers with nowhere to see which policies they are is no record. -->
              <p v-if="kindOf(run) === 'PAYMENTS' && finished(run) && policiesReadOf(run) !== 0" class="run-list__all">
                <button class="run-list__lnk run-list__lnk--inline" type="button" @click.stop="seeAllPolicies(run)">
                  {{ policiesReadOf(run) === null
                    ? $t('carriers.import.runRows.SeeAll')
                    : $t('carriers.import.runRows.SeeAllCount', { count: formatCount(policiesReadOf(run)) }) }}
                </button>
              </p>

              <p v-if="fixHint(run)" class="run-list__fix">{{ fixHint(run) }}</p>
              <!-- The fix for a rejected password, right under the sentence that asks for it. Only
                   while the login still fails: not on a failure the password has been changed since. -->
              <div v-if="passwordAccountOf(run)" class="run-list__fix-action" @click.stop>
                <BaseButton is-small @click="changingPassword = passwordAccountOf(run)">
                  {{ $t('carriers.import.password.Update') }}
                </BaseButton>
              </div>

              <div v-if="expanded === read.key" class="run-list__more" @click.stop>
                <!-- A read started from Carriers wrote nothing: what it found is
                     held for review, so the body offers the decision rather than a
                     link to policies that do not exist yet. -->
                <!-- Every person the read touched: how many, and of them how many it made, recognised
                     by name rather than by RFC/e-mail/phone, or corrected. A run from before the
                     per-person record only knows how many rows it wrote. -->
                <div v-if="run.clientCounts || run.clientChangesCount" class="run-list__ml">
                  <span class="run-list__icon run-list__icon--policies">✎</span>
                  <div>
                    <b v-if="run.clientCounts">{{ $t('carriers.import.clientChanges.Clients', { count: formatCount(run.clientCounts.clients) }) }}</b>
                    <b v-else>{{ $t('carriers.import.clientChanges.Line', { count: formatCount(run.clientChangesCount) }) }}</b>
                    <p class="run-list__ml-sub">{{ clientCountsLine(run) || $t('carriers.import.clientChanges.Hint') }}</p>
                  </div>
                  <button class="run-list__lnk" type="button" @click="clientChangesOf = run.scrapeJobId">
                    {{ $t('carriers.import.clientChanges.SeeAll') }}
                  </button>
                </div>

                <template v-if="isSnapshotRun(run)">
                  <div
                    v-for="group in snapshotGroups(run)"
                    :key="group.kind"
                    class="run-list__ml"
                  >
                    <span class="run-list__icon" :class="`run-list__icon--${group.tone}`">
                      {{ group.glyph }}
                    </span>
                    <div>
                      <b>{{ $t(`carriers.import.snapshot.group.${group.kind}`) }}</b>
                      <p class="run-list__ml-sub">{{ group.count }}</p>
                    </div>
                    <button
                      v-if="group.count && group.kind !== 'SUPERSEDED'"
                      class="run-list__lnk"
                      type="button"
                      @click="review(run, group.kind)"
                    >
                      {{ $t(`carriers.import.snapshot.action.${group.kind}`) }}
                    </button>
                  </div>
                </template>

                <template v-else>
                <!-- A payments read creates and updates no policies, so it has no
                     policies line to show. -->
                <div v-if="kindOf(run) !== 'PAYMENTS'" class="run-list__ml">
                  <span class="run-list__icon run-list__icon--policies">▤</span>
                  <div>
                    <b>{{ $t('carriers.import.kind.POLICIES') }}</b>
                    <p class="run-list__ml-sub">{{ policiesDetail(run) }}</p>
                  </div>
                  <button class="run-list__lnk" type="button" @click="openInPolicies(run)">
                    {{ $t('carriers.import.history.OpenInPolicies') }}
                  </button>
                </div>

                <!-- Only a payment run has a reconcile to report. Asking about a
                     policy crawl would answer about whatever payment run came
                     last, which is not this one. -->
                <!-- Not for a read whose only open work is held AXA policies: "Nothing left to decide"
                     beside them would contradict the list below. -->
                <div v-if="kindOf(run) === 'PAYMENTS' && !heldOnly(run)" class="run-list__ml">
                  <span class="run-list__icon run-list__icon--payments">$</span>
                  <div>
                    <b>{{ $t('carriers.import.kind.PAYMENTS') }}</b>
                    <p class="run-list__ml-sub">{{ paymentsDetail(run) }}</p>
                  </div>
                  <button
                    v-if="paymentsPending(run)"
                    class="run-list__lnk"
                    type="button"
                    @click="reviewPayments(run)"
                  >
                    {{ $t('carriers.import.needs.action.MARK_PAID') }}
                  </button>
                </div>

                <template v-if="kindOf(run) === 'PAYMENTS'">
                  <!-- AXA policies the automatic update left for a person: decided right here. -->
                  <div v-if="heldCount(run)" class="run-list__ml">
                    <span class="run-list__icon run-list__icon--warn">?</span>
                    <div class="run-list__held">
                      <b>{{ $t('carriers.import.held.Title', { count: formatCount(heldCount(run)) }) }}</b>
                      <CarrierHeldSchedules :run="run" @resolved="onHeldResolved(run)"/>
                    </div>
                  </div>

                  <!-- What the read changed by itself: AXA's receipts are the truth, so its schedules
                       are brought in line without a review. The run behind them shows each policy
                       before and after, and undoes them one by one or all at once. -->
                  <div v-if="changesRunId(run)" class="run-list__ml">
                    <span class="run-list__icon run-list__icon--payments">↻</span>
                    <div>
                      <b>
                        <template v-if="changedCount(run) !== null">{{ formatCount(changedCount(run)) }}</template>
                        {{ $t('carriers.import.summary.move.synced') }}
                      </b>
                      <p class="run-list__ml-sub">{{ $t('carriers.import.paymentsLine.UndoHint') }}</p>
                    </div>
                    <button class="run-list__lnk" type="button" @click="seeChanges(run)">
                      {{ $t('carriers.import.summary.SeeWhatChanged') }}
                    </button>
                  </div>

                  <div v-if="stoppedPolicies(run).length" class="run-list__ml">
                    <span class="run-list__icon run-list__icon--warn">!</span>
                    <div>
                      <b>{{ $t('carriers.import.paymentsLine.StoppedBillingBy', { carrier: run.carrierName || $t('carriers.import.TheCarrier') }) }}</b>
                      <p
                        v-for="policy in stoppedPolicies(run)"
                        :key="policy.insurancePolicyId || policy.policyNumber"
                        class="run-list__ml-sub"
                      >
                        {{ policy.policyNumber }}<template v-if="policy.clientName"> — {{ policy.clientName }}</template>
                      </p>
                    </div>
                  </div>

                  <div v-if="failedCount(run)" class="run-list__ml">
                    <span class="run-list__icon run-list__icon--warn">⚠</span>
                    <div>
                      <b>{{ formatCount(failedCount(run)) }} {{ $t('carriers.import.paymentsLine.failed') }}</b>
                    </div>
                  </div>

                  <!-- Every receipt the read saw, with what became of its policy, as one file. -->
                  <div class="run-list__ml">
                    <button
                      class="run-list__lnk"
                      type="button"
                      :disabled="busy === `refunds-${run.scrapeJobId}`"
                      @click="downloadRefunds(run)"
                    >
                      {{ $t('carriers.import.payments.DownloadAllPayments') }}
                    </button>
                  </div>
                </template>

                </template>
              </div>
            </div>
          </div>

          <div v-if="canRunImport" class="run-list__actions" @click.stop>
            <template v-for="run in read.runs">
              <BaseButton
                v-if="isJobActive(run) && !isStopping(run) && !runsInBackend(run)"
                :key="`stop-${run.scrapeJobId}`"
                is-small
                type="outline"
                :loading="busy === run.scrapeJobId"
                @click="stop(run)"
              >
                {{ $t('carriers.import.Stop') }}<template v-if="read.runs.length > 1"> · {{ $t(`carriers.import.kind.${kindOf(run)}`) }}</template>
              </BaseButton>
              <BaseButton
                v-else-if="canResume(run)"
                :key="`go-${run.scrapeJobId}`"
                is-small
                :loading="busy === run.scrapeJobId"
                @click="proceed(run)"
              >
                {{ $t('carriers.import.Proceed') }}<template v-if="read.runs.length > 1"> · {{ $t(`carriers.import.kind.${kindOf(run)}`) }}</template>
              </BaseButton>
            </template>
          </div>
        </div>
      </div>
    </div>

    <SnapshotReviewModal
      v-if="reviewing"
      :run="reviewing.run"
      :kind="reviewing.kind"
      :carrier-name="reviewing.run.carrierName || $t('carriers.import.TheCarrier')"
      @resolved="onResolved(reviewing.run)"
      @close="reviewing = null"
    />

    <!-- The import run a payments read applied, as Payments → Import history shows it. It stacks
         over the drawer like the snapshot review above. -->
    <!-- Every policy a payments read went through. Stacks over the drawer like the two above. -->
    <PaymentRunPoliciesModal
      v-if="allPoliciesOf"
      :run="allPoliciesOf"
      :carrier-name="allPoliciesOf.carrierName || $t('carriers.import.TheCarrier')"
      @close="allPoliciesOf = null"
    />

    <ClientChangesRunModal
      v-if="clientChangesOf"
      :scrape-job-id="clientChangesOf"
      @close="clientChangesOf = null"
    />

    <PaymentImportRunModal
      v-if="changesOf"
      :run-id="changesOf.runId"
      @undone="onUndone(changesOf.run)"
      @close="changesOf = null"
    />

    <UpdatePasswordDialog
      v-if="changingPassword"
      :account="changingPassword"
      @close="changingPassword = null"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import BaseButton from '@/views/RenovaDashboard/components/ui-kit/Buttons/BaseButton';
import BasePreloader from '@/views/RenovaDashboard/components/ui-kit/Buttons/BasePreloader';
import { UserPermissions } from '@/utils/permissions';
import {
  apiError, canResume, etaText, formatCount, formatWhen, groupReads, isDailyRun, isJobActive, isSnapshotRun, isStopping,
  jobKind, needsNewPassword, runsInBackend, paymentsLine, progressFigure, progressTotalKey, runProgress, runVariant, saveFile, variantTone
} from '@/utils/carrierImport';
import { formatCarrierMoney } from '@/utils/carrierFormat';
import { SYNC_SCHEDULE } from '@/utils/carrierPayments';
import PaymentImportRunModal from '@/views/RenovaDashboard/PaymentsParts/ImportHistory/PaymentImportRunModal';
import CarrierHeldSchedules from './CarrierHeldSchedules';
import CarrierLogo from './CarrierLogo';
import ClientChangesRunModal from './ClientChangesRunModal';
import SnapshotReviewModal from './SnapshotReviewModal';
import PaymentRunPoliciesModal from './PaymentRunPoliciesModal';
import UpdatePasswordDialog from './UpdatePasswordDialog';

/**
 * The runs of one portal account, or of the whole tenant — the same rows either
 * way, so an account's own history and the All updates drawer cannot drift apart
 * in how they describe a run.
 *
 * A row reads as the carrier's own summary: what it read, what that changed, and
 * where to go and look at it. The per-policy skip table underneath is fetched
 * lazily, and only when someone asks why — a large book carries 500 summary rows
 * and nobody opening the drawer wanted them all.
 */
export default {
  name: 'ImportRunList',
  components: {
    BaseButton,
    BasePreloader,
    CarrierHeldSchedules,
    CarrierLogo,
    ClientChangesRunModal,
    PaymentImportRunModal,
    PaymentRunPoliciesModal,
    SnapshotReviewModal,
    UpdatePasswordDialog,
  },
  mixins: [UserPermissions],
  props: {
    runs: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    /** Name the carrier and the login on each row — All updates does, one account's history does not. */
    showWho: { type: Boolean, default: false },
    /** `run -> logo URL or null` for the carrier on each row when `showWho`; null shows the placeholder. */
    logoFor: { type: Function, default: null },
    /** Overrides the empty line — All updates says so when its carrier filter matches nothing. */
    emptyText: { type: String, default: null },
    /**
     * The run to mark as the one just returned from, as `{ scrapeJobId }`.
     *
     * A list you come back to should say where you were; without it a drawer that reopens on the
     * same scroll position still leaves you re-finding the row you had just finished.
     */
    highlight: { type: Object, default: null },
  },
  emits: ['reviewPayments', 'resolved'],
  data() {
    return {
      expanded: null,
      busy: null,
      reviewing: null,
      // scrapeJobId -> snapshot/summary, fetched when a snapshot run is opened.
      summaries: {},
      // scrapeJobId -> the payment reconcile summary, fetched when a payments run is opened.
      paymentSummaries: {},
      // The import run open in PaymentImportRunModal, as `{ run, runId }`.
      changesOf: null,
      // The scrapeJobId whose client changes are open in ClientChangesRunModal.
      clientChangesOf: null,
      // The payments run whose every policy is listed in PaymentRunPoliciesModal.
      allPoliciesOf: null,
      // The portal account whose password is being replaced in UpdatePasswordDialog.
      changingPassword: null,
    };
  },
  computed: {
    ...mapGetters(['carrierImportAccounts', 'carrierImportRuns']),
    /** Stopping and resuming a run are policy writes, like starting one. */
    canRunImport() {
      return this.checkUserPermissions([this.PermissionsTypes.POLICIES_UPDATE]);
    },
    /** The rows: the runs as reads, the two jobs of a "Read both" as one (groupReads). */
    reads() {
      return groupReads(this.runs);
    },
  },
  methods: {
    isDailyRun,
    /**
     * A decision in the review moves more than this list's own counts: a client
     * named for a skipped policy turns it into a new one waiting, which the
     * page's summary card has to show too.
     */
    onResolved(run) {
      this.refreshSummary(run);
      this.$emit('resolved', run);
    },
    isJobActive,
    isStopping,
    isSnapshotRun,
    canResume,
    runsInBackend,
    /**
     * What a staged read holds: the two decisions -- what it would create, and
     * what it would correct -- and two lists to read.
     *
     * The lists are SKIPPED rows split by why: policies the carrier shows on the
     * client that the client's own portal page does not, which as far as anyone
     * here can tell are held with other agents; and policies the read could not
     * use at all. Neither is a decision, so their review opens without buttons,
     * and a list with nothing in it is not shown.
     */
    snapshotGroups(run) {
      const s = this.summaries[run.scrapeJobId] || {};
      const otherAgents = s.otherAgentsCount || 0;
      const couldNotRead = Math.max((s.skippedCount || 0) - otherAgents, 0);
      return [
        {
          kind: 'NEW', count: s.newCount || 0, glyph: '▤', tone: 'policies'
        },
        {
          kind: 'UPDATE', count: s.updateCount || 0, glyph: '↻', tone: 'payments'
        },
        {
          kind: 'OTHER_AGENTS', count: otherAgents, glyph: '⇄', tone: 'policies'
        },
        {
          kind: 'SKIPPED', count: couldNotRead, glyph: '⚠', tone: 'warn'
        },
        {
          // Rows a newer read of the same account staged again and took over: no longer this
          // run's to decide, said so it is clear why the counts above do not add up to them.
          kind: 'SUPERSEDED', count: s.supersededCount || 0, glyph: '⇢', tone: 'warn'
        },
      ].filter((g) => g.count || ['NEW', 'UPDATE'].includes(g.kind));
    },
    /** The groups still waiting on a person, by group. Empty until the summary is in. */
    pendingOf(run) {
      const s = this.paymentSummaries[run.scrapeJobId];
      return (s && s.pendingByGroup) || null;
    },
    /**
     * Rows still waiting. Each group defaults to none: a group the server left
     * out made the sum NaN, which read as "Nothing left to decide".
     */
    pendingTotal(c) {
      return (c.MARK_PAID || 0) + (c.CANCEL_POLICY || 0) + (c.NO_MATCH || 0);
    },
    /** "12 to mark paid · 3 cancelled · 6 unmatched", or what is known so far. */
    paymentsDetail(run) {
      const c = this.pendingOf(run);
      if (!c) return this.$t('carriers.import.payments.Loading');
      if (!this.pendingTotal(c)) return this.$t('carriers.import.payments.AllSettled');
      return this.$t('carriers.import.payments.Detail', {
        paid: formatCount(c.MARK_PAID),
        cancelled: formatCount(c.CANCEL_POLICY),
        unmatched: formatCount(c.NO_MATCH),
      });
    },
    paymentsPending(run) {
      const c = this.pendingOf(run);
      return Boolean(c && this.pendingTotal(c));
    },
    /** AXA policies held for a decision on this run. */
    heldCount(run) {
      const c = this.pendingOf(run);
      return (c && c[SYNC_SCHEDULE]) || 0;
    },
    heldOnly(run) {
      const c = this.pendingOf(run);
      return Boolean(c && this.heldCount(run) && !this.pendingTotal(c));
    },
    /** A decision moves this run's counts and the page's card. */
    onHeldResolved(run) {
      this.loadPayments(run, true);
      this.$emit('resolved', run);
    },
    paymentsLineOf: paymentsLine,
    /** Done with, whether or not it changed anything: the runs a payments line describes. */
    finished(run) {
      return ['completed', 'nothingChanged'].includes(runVariant(run));
    },
    /** The import run this read applied by itself, if it applied anything. */
    changesRunId(run) {
      const own = run.paymentCounts && run.paymentCounts.autoRunId;
      if (own) return own;
      const s = this.paymentSummaries[run.scrapeJobId];
      const synced = s && s.settled && s.settled[SYNC_SCHEDULE];
      return (synced && synced.runId) || null;
    },
    /** How many policies that run changed, or null when neither side says. */
    changedCount(run) {
      const own = run.paymentCounts && run.paymentCounts.policiesChanged;
      if (own !== null && own !== undefined) return own;
      const s = this.paymentSummaries[run.scrapeJobId];
      const synced = s && s.settled && s.settled[SYNC_SCHEDULE];
      return synced && synced.count !== undefined ? synced.count : null;
    },
    stoppedPolicies(run) {
      const s = this.paymentSummaries[run.scrapeJobId];
      return (s && s.stoppedPolicies) || [];
    },
    failedCount(run) {
      const s = this.paymentSummaries[run.scrapeJobId];
      if (s && s.failedCount !== undefined && s.failedCount !== null) return s.failedCount;
      return (run.paymentCounts && run.paymentCounts.failed) || 0;
    },
    money(amount) {
      return formatCarrierMoney(amount);
    },
    /** How many policies the read went through, from its own counts; null for a run from before them. */
    policiesReadOf(run) {
      const n = run.paymentCounts && run.paymentCounts.policiesRead;
      return n === null || n === undefined ? null : Number(n) || 0;
    },
    seeAllPolicies(run) {
      this.allPoliciesOf = run;
    },
    seeChanges(run) {
      this.changesOf = { run, runId: this.changesRunId(run) };
    },
    /** An undo moves the run's counts on the page's card as much as here. */
    onUndone(run) {
      this.loadPayments(run, true);
      this.$emit('resolved', run);
    },
    downloadRefunds(run) {
      this.busy = `refunds-${run.scrapeJobId}`;
      this.$store.dispatch('carrierImportExportRefundReceipts', { scrapeJobId: run.scrapeJobId, jobType: run.jobType })
        .then(({ blob, fileName }) => saveFile(blob, fileName))
        .catch((err) => this.$notify.error({ message: apiError(err, this.$t('adminTitles.someError')) }))
        .finally(() => { this.busy = null; });
    },
    /**
     * Which group to open: whichever has something, most actionable first.
     *
     * Handed up rather than opened here. This list is the body of a drawer, and a review mounted
     * from inside it could only be a second drawer over the first -- which buried the list the
     * operator was working through. The drawer renders it in its own place instead.
     */
    reviewPayments(run) {
      const c = this.pendingOf(run) || {};
      const group = ['MARK_PAID', 'CANCEL_POLICY', 'NO_MATCH'].find((g) => c[g] > 0);
      if (group) this.$emit('reviewPayments', { run, group });
    },
    /** The run just returned from, so coming back shows where you were. */
    isCurrent(run) {
      return Boolean(this.highlight && this.highlight.scrapeJobId === run.scrapeJobId);
    },
    /** Called by the drawer when a review it opened from here has been resolved. */
    refreshPayments(run) {
      return this.loadPayments(run, true);
    },
    /**
     * Counted by the server, over the whole run.
     *
     * This used to fetch one page of at most 200 rows and count them here, so
     * a group above that was simply undercounted -- and it disagreed with the
     * same run's counts on the Carriers card, which asks the summary.
     */
    loadPayments(run, force = false) {
      if (!force && this.paymentSummaries[run.scrapeJobId]) return Promise.resolve();
      return this.$store.dispatch('carrierImportFetchPaymentSummary', run.scrapeJobId)
        .then((data) => {
          this.paymentSummaries = {
            ...this.paymentSummaries,
            [run.scrapeJobId]: { ...(data || {}), pendingByGroup: (data && data.pendingByGroup) || {} },
          };
        })
        .catch(() => {});
    },
    refreshSummary(run) {
      return this.$store.dispatch('carrierImportFetchSnapshotSummary', run.scrapeJobId)
        .then((data) => {
          // Replaced, not mutated: Vue 2 does not see a key added to an object.
          this.summaries = { ...this.summaries, [run.scrapeJobId]: data || {} };
        })
        .catch(() => {});
    },
    review(run, kind) {
      this.reviewing = { run, kind };
    },
    formatWhen,
    formatCount,
    kindOf: jobKind,
    tone(run) {
      return variantTone(runVariant(run));
    },
    /** ✓ when the run did its job, ! when it did not. */
    badge(run) {
      return this.tone(run) === 'success' ? '✓' : '!';
    },
    /** Only a run that got far enough to have something to show opens. */
    /** "Payments", "Policies", or both for a read of two. */
    kindLabel(read) {
      return read.runs.length > 1
        ? this.$t('carriers.import.kind.BOTH')
        : this.$t(`carriers.import.kind.${jobKind(read.lead)}`);
    },
    /** A read opens when any of its halves has something to open. */
    expandable(read) {
      return read.runs.some((run) => this.expandableRun(run));
    },
    expandableRun(run) {
      if (isJobActive(run)) return false;
      // A staged run has written nothing, so it has no imported counts to go on —
      // what it holds is the reason to open it. A payments read always opens:
      // its figures are in its reconcile summary, which only opening asks for.
      return isSnapshotRun(run) || jobKind(run) === 'PAYMENTS' || this.hasCounts(run);
    },
    progressOf: runProgress,
    progressFigure,
    progressTotalKey,
    /**
     * What to say while the size of the book is still unknown.
     *
     * True whether the worker has simply not sent its first part yet — which on a
     * portal that emails a sign-in code is minutes — or this frontend is talking
     * to a backend that does not report position at all. Payments has no book to
     * size, so it gets the plain running state instead.
     */
    unknownProgressText(run) {
      return jobKind(run) === 'PAYMENTS'
        ? this.$t('carriers.import.state.running')
        : this.$t('carriers.import.running.SizingUp');
    },
    etaOf(run) {
      return etaText(run, this.$t.bind(this));
    },
    /** A finished run that read part of the book and stopped. */
    stoppedEarly(run) {
      return !isJobActive(run) && runVariant(run) === 'cancelled';
    },
    hasCounts(run) {
      return Number(run.totalReadCount) > 0 || Number(run.importedCount) > 0;
    },
    /** A run recorded after the counts were split into read/created/updated. */
    hasFullCounts(run) {
      return Number(run.totalReadCount) > 0 && run.policiesCreatedCount != null;
    },
    /** "incl. N member policies (M updated)" for a run that wrote any; '' otherwise. */
    /** "9 new · 25 recognised by name · 25 RFCs corrected", or nothing when the run only recognised people the usual way. */
    clientCountsLine(run) {
      const c = run.clientCounts;
      if (!c) return '';
      return [
        c.created ? this.$t('carriers.import.clientChanges.New', { count: formatCount(c.created) }) : null,
        c.matchedByName ? this.$t('carriers.import.clientChanges.ByName', { count: formatCount(c.matchedByName) }) : null,
        c.rfcCorrected ? this.$t('carriers.import.clientChanges.RfcCorrected', { count: formatCount(c.rfcCorrected) }) : null,
      ].filter(Boolean).join(' · ');
    },
    memberPoliciesLine(run) {
      const created = Number(run.memberPoliciesCreated) || 0;
      const updated = Number(run.memberPoliciesUpdated) || 0;
      if (!created && !updated) return '';
      return this.$t('carriers.import.history.MemberPolicies', {
        added: formatCount(created),
        updated: formatCount(updated),
      });
    },
    policiesDetail(run) {
      const created = Number(run.policiesCreatedCount);
      return this.$t('carriers.import.history.PoliciesDetail', {
        added: formatCount(Number.isFinite(created) ? created : run.importedCount),
        updated: formatCount(run.policiesUpdatedCount),
        leftOut: formatCount(run.skippedCount),
      });
    },
    /** The sentence under a failure that says what to do about it. */
    fixHint(run) {
      const variant = runVariant(run);
      if (variant === 'twoFaTimeout') return this.$t('carriers.import.fix.twoFaTimeout');
      if (variant === 'authFailed') return this.$t('carriers.import.fix.authFailed');
      if (variant === 'rateLimited') return this.$t('carriers.import.fix.rateLimited');
      if (variant === 'twoFaRequired' || variant === 'twoFaSetupRequired') {
        return this.$t(`carriers.import.fix.${variant}`, {
          carrier: run.carrierName || this.$t('carriers.import.TheCarrier'),
        });
      }
      return '';
    },
    /**
     * The login to offer "Update password" for under a rejected-password run: the run's own, while
     * the portal still rejects it. Changing a password is a policy write, like starting a read.
     */
    passwordAccountOf(run) {
      if (!this.canRunImport || runVariant(run) !== 'authFailed') return null;
      const account = (this.carrierImportAccounts || []).find((a) => a.scrapeAccountId === run.scrapeAccountId);
      return needsNewPassword(account, (this.carrierImportRuns || {}).items) ? account : null;
    },
    /** Why a run that produced no counts ended the way it did. */
    line(run) {
      const variant = runVariant(run);
      // 'stopping' is deliberately absent: it is already in the head, and saying
      // it twice on one row read as a stutter.
      if (variant === 'running' || variant === 'stopping') {
        return this.$t('carriers.import.state.running');
      }
      if (variant === 'completed' || variant === 'nothingChanged') {
        return this.$t('carriers.import.history.NoItems');
      }
      const why = this.$t(`carriers.import.variant.${variant}`, {
        carrier: run.carrierName || this.$t('carriers.import.TheCarrier'),
      });
      // The worker's own words for a rejected password are a technical English line; the sentence
      // above and the fix under it already say everything an agent can act on. Same for a code
      // the authenticator app was never set up for or nobody was there to type.
      if (['authFailed', 'twoFaRequired', 'twoFaSetupRequired'].includes(variant)) return why;
      const detail = run.errorMessage || run.importError || '';
      return detail ? `${why} — ${detail}` : why;
    },
    /**
     * The Policies page, filtered to this run, in a new tab.
     *
     * A run id rather than the run's policy ids: a whole-book read imports
     * thousands and that list belongs in no query string. The page resolves it
     * against the backend's scrapeJobIds filter.
     */
    openInPolicies(run) {
      const { href } = this.$router.resolve({
        path: '/admin/policies',
        // runAt only names the chip over there; the filter is the id.
        query: { scrapeJobId: run.scrapeJobId, runAt: run.finishedAt || run.updatedAt },
      });
      window.open(href, '_blank', 'noopener');
    },
    stop(run) {
      this.busy = run.scrapeJobId;
      this.$store.dispatch('carrierImportStop', run.scrapeJobId)
        .then(() => this.$notify.success({ message: this.$t('carriers.import.StoppingHint') }))
        .catch((err) => this.$notify.error({ message: apiError(err, this.$t('adminTitles.someError')) }))
        .finally(() => { this.busy = null; });
    },
    proceed(run) {
      this.busy = run.scrapeJobId;
      this.$store.dispatch('carrierImportProceed', run.scrapeJobId)
        .then(() => this.$notify.success({ message: this.$t('carriers.import.ProceedStarted') }))
        .catch((err) => this.$notify.error({ message: apiError(err, this.$t('adminTitles.someError')) }))
        .finally(() => { this.busy = null; });
    },
    toggle(read) {
      if (!this.expandable(read)) return;
      if (this.expanded === read.key) {
        this.expanded = null;
        return;
      }
      this.expanded = read.key;
      read.runs.forEach((run) => {
        if (isSnapshotRun(run) && !this.summaries[run.scrapeJobId]) this.refreshSummary(run);
        if (jobKind(run) === 'PAYMENTS') this.loadPayments(run);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
// Success, warning and danger text have no token in the app's palette — these
// three are the design's own, and are the only literals here.
$ink-success: #2e7d32;
$ink-warning: #996c00;

.run-list {
  &__empty {
    padding: 24px 0;
    font-size: 14px;
    color: var(--solid-dark-purple);
  }

  &__run {
    border-bottom: 1px solid var(--solid-lavender-grey);

    &:last-child { border-bottom: 0; }

    // The row just returned from. An inset rule rather than a border: the row already has a
    // bottom border, and a real one on the left would shift every line in by its width the
    // moment you came back.
    &--current > .run-list__head {
      box-shadow: inset 3px 0 0 var(--solid-purple);
      background: var(--solid-lavender-ligth);
    }
  }

  &__head {
    display: flex;
    align-items: flex-start;
    gap: 13px;
    padding: 16px 0;
    cursor: pointer;
  }

  &__badge {
    width: 26px;
    height: 26px;
    border-radius: 7px;
    display: grid;
    place-items: center;
    font-size: 12px;
    font-weight: 700;
    flex: none;
    background: var(--solid-grey-light);
    color: var(--solid-dark-purple);

    &--success { background: rgba(88, 219, 77, 0.16); color: $ink-success; }
    &--warning { background: var(--solid-milk-ornage); color: $ink-warning; }
    &--info { background: var(--solid-light-purple); color: var(--solid-purple); }
  }

  &__main { flex: 1; min-width: 0; }

  &__when {
    font-size: 15px;
    font-weight: 600;
    color: var(--solid-black-purple);
    letter-spacing: -0.015em;
  }

  &__kind,
  &__live {
    font-size: 13px;
    font-weight: 400;
    color: var(--solid-dark-purple);
  }

  &__daily {
    display: inline-block;
    margin-left: 6px;
    padding: 0 7px;
    border-radius: 100px;
    font-size: 11px;
    font-weight: 500;
    background: #eeebfd;
    color: #6f5ce6;
    vertical-align: 1px;
  }

  &__partial {
    font-size: 13px;
    font-weight: 500;
    color: var(--solid-orange);
  }

  &__chevron {
    color: var(--solid-dark-purple);
    font-size: 11px;
    margin-left: 8px;
  }

  // The carrier leads the row's second line, mark and name, so a list of several reads by eye.
  &__who {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 6px;
    min-width: 0;
    font-size: 13px;
  }

  &__carrier {
    font-weight: 600;
    color: var(--solid-black-purple);
    white-space: nowrap;
  }

  &__login {
    color: var(--solid-dark-purple);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  // The second half of a read of two sits under the first, ruled off, each under its name.
  &__half--paired {
    margin-top: 8px;
    padding-left: 10px;
    border-left: 2px solid var(--solid-lavender-grey);
  }

  &__half-kicker {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: var(--solid-dark-purple);
    margin-bottom: 2px;
  }

  &__sum {
    font-size: 13.5px;
    line-height: 1.55;
    color: var(--solid-black-purple-70);
    margin-top: 7px;
    font-variant-numeric: tabular-nums;

    b {
      font-weight: 600;
      color: var(--solid-black-purple);
    }
  }

  &__muted { color: var(--solid-dark-purple); }

  // An orange rule rather than a bordered card: the hint belongs to the row
  // above it, and a box would read as a separate thing to deal with.
  &__fix {
    font-size: 13.5px;
    line-height: 1.55;
    color: var(--solid-black-purple-70);
    margin-top: 7px;
    border-left: 2px solid var(--solid-orange);
    padding-left: 12px;
  }

  // Under the fix sentence, indented with its text past the rule.
  &__fix-action {
    margin-top: 8px;
    padding-left: 14px;
  }

  &__more {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid var(--solid-lavender-grey);
    display: flex;
    flex-direction: column;
    gap: 8px;
    cursor: default;
  }

  &__held {
    flex: 1;
    min-width: 0;
  }

  &__ml {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: 13.5px;
    color: var(--solid-black-purple);

    b { font-weight: 700; }

    &--soon {
      opacity: 0.55;
      cursor: not-allowed;
    }
  }

  &__ml-sub {
    font-size: 12.5px;
    color: var(--solid-dark-purple);
    margin-top: 3px;
  }

  &__icon {
    width: 16px;
    text-align: center;
    flex: none;
    line-height: 1.4;

    &--policies { color: var(--solid-purple); }
    &--payments { color: var(--solid-green); }
    &--warn { color: var(--solid-orange); }
  }

  &__lnk {
    margin-left: auto;
    background: none;
    border: 0;
    padding: 0;
    cursor: pointer;
    color: var(--solid-purple);
    font-weight: 600;
    font-size: 13px;
    white-space: nowrap;
  }

  &__all { margin-top: 4px; }

  // In a line of its own under the counts, not pushed to the right edge like a row action.
  &__lnk--inline { margin-left: 0; }

  &__actions {
    display: flex;
    align-items: center;
    gap: 6px;
    flex: none;
  }

  &__skips { padding-top: 4px; }

  &__note {
    font-size: 12px;
    color: var(--solid-dark-purple);
    padding-top: 8px;
  }

  &__table {
    width: 100%;
    font-size: 12px;
    margin-top: 8px;

    td {
      padding: 4px 8px 4px 0;
      vertical-align: top;
      color: var(--solid-black-purple);
    }
  }

  &__outcome {
    font-weight: 600;
    white-space: nowrap;

    &--created { color: $ink-success; }
    &--updated { color: var(--solid-purple); }
    &--skipped { color: $ink-warning; }
  }

  &__reason { color: var(--solid-dark-purple); }

  &__link { color: var(--solid-purple); }
}
</style>
